import merge from 'lodash/merge';

const KEY_PREFIX = 'cshub:embed-preview-session:v1:';

/** Previous localStorage prefix (question-bank preview) — migrated on read. */
const LEGACY_LS_PREFIX = 'cshub:qb-view-session:v1:';

/** Max serialized length for `qbUa` embed query param (browser URL limits). */
export const EMBED_USER_ANSWERS_MAX_URL_CHARS = 4000;

/** @deprecated Use EMBED_USER_ANSWERS_MAX_URL_CHARS */
export const QB_UA_MAX_URL_CHARS = EMBED_USER_ANSWERS_MAX_URL_CHARS;

export interface EmbedPreviewSessionCache {
    session_id: string;
    launch_token: string | null;
    /** True after embed reports SESSION_READY (ACTIVE — resume without launch token). */
    activated: boolean;
    /** Deep-merged session `user_answers` (API shape); persists in localStorage. */
    user_answers: Record<string, unknown> | null;
}

/** Optional filters for {@link readEmbedPreviewSession}. */
export interface ReadEmbedPreviewSessionOptions {
    /**
     * When set, a cached row whose `session_id` differs is treated as stale:
     * storage is cleared and `null` is returned so a new session does not
     * hydrate answers from another session for the same `contentId`.
     */
    expectedSessionId?: string | null;
}

function discardStaleEmbedPreviewSession(
    contentId: string,
    cached: EmbedPreviewSessionCache | null,
    expectedSessionId: string | null | undefined,
): EmbedPreviewSessionCache | null {
    if (!cached) {
        return null;
    }
    const expected = typeof expectedSessionId === 'string' ? expectedSessionId.trim() : '';
    if (expected.length > 0 && cached.session_id !== expected) {
        clearEmbedPreviewSession(contentId);
        return null;
    }
    return cached;
}

function storageKey(contentId: string): string {
    return `${KEY_PREFIX}${contentId}`;
}

/** Legacy sessionStorage key (same suffix as v1 qb-view). */
const LEGACY_SESSION_KEY_PREFIX = 'cshub:qb-view-session:v1:';

function migrateFromLegacySessionStorage(contentId: string): EmbedPreviewSessionCache | null {
    if (typeof sessionStorage === 'undefined') {
        return null;
    }
    try {
        const raw = sessionStorage.getItem(`${LEGACY_SESSION_KEY_PREFIX}${contentId}`);
        if (!raw) {
            return null;
        }
        sessionStorage.removeItem(`${LEGACY_SESSION_KEY_PREFIX}${contentId}`);
        const parsed = JSON.parse(raw) as Partial<EmbedPreviewSessionCache>;
        if (!parsed.session_id) {
            return null;
        }
        const migrated: EmbedPreviewSessionCache = {
            session_id: parsed.session_id,
            launch_token: parsed.launch_token ?? null,
            activated: Boolean(parsed.activated),
            user_answers:
                parsed.user_answers && typeof parsed.user_answers === 'object'
                    ? (parsed.user_answers as Record<string, unknown>)
                    : null,
        };
        writeEmbedPreviewSession(contentId, migrated);
        return migrated;
    } catch {
        return null;
    }
}

export function readEmbedPreviewSession(
    contentId: string,
    options?: ReadEmbedPreviewSessionOptions,
): EmbedPreviewSessionCache | null {
    const expected = options?.expectedSessionId;
    if (typeof localStorage === 'undefined') {
        return discardStaleEmbedPreviewSession(
            contentId,
            migrateFromLegacySessionStorage(contentId),
            expected,
        );
    }
    try {
        const raw = localStorage.getItem(storageKey(contentId));
        if (!raw) {
            const legacyLs = localStorage.getItem(`${LEGACY_LS_PREFIX}${contentId}`);
            if (legacyLs) {
                try {
                    localStorage.removeItem(`${LEGACY_LS_PREFIX}${contentId}`);
                } catch {
                    // ignore
                }
                const parsedLegacy = JSON.parse(legacyLs) as Partial<EmbedPreviewSessionCache>;
                if (parsedLegacy.session_id) {
                    const migrated: EmbedPreviewSessionCache = {
                        session_id: parsedLegacy.session_id,
                        launch_token: parsedLegacy.launch_token ?? null,
                        activated: Boolean(parsedLegacy.activated),
                        user_answers:
                            parsedLegacy.user_answers && typeof parsedLegacy.user_answers === 'object'
                                ? (parsedLegacy.user_answers as Record<string, unknown>)
                                : null,
                    };
                    writeEmbedPreviewSession(contentId, migrated);
                    return discardStaleEmbedPreviewSession(contentId, migrated, expected);
                }
            }
        }
        if (!raw) {
            return discardStaleEmbedPreviewSession(
                contentId,
                migrateFromLegacySessionStorage(contentId),
                expected,
            );
        }
        const parsed = JSON.parse(raw) as EmbedPreviewSessionCache;
        if (!parsed.session_id) {
            return null;
        }
        const normalized: EmbedPreviewSessionCache = {
            session_id: parsed.session_id,
            launch_token: parsed.launch_token ?? null,
            activated: Boolean(parsed.activated),
            user_answers:
                parsed.user_answers && typeof parsed.user_answers === 'object'
                    ? (parsed.user_answers as Record<string, unknown>)
                    : null,
        };
        return discardStaleEmbedPreviewSession(contentId, normalized, expected);
    } catch {
        return discardStaleEmbedPreviewSession(
            contentId,
            migrateFromLegacySessionStorage(contentId),
            expected,
        );
    }
}

export function writeEmbedPreviewSession(contentId: string, data: EmbedPreviewSessionCache): void {
    if (typeof localStorage === 'undefined') {
        return;
    }
    try {
        localStorage.setItem(storageKey(contentId), JSON.stringify({ ...data, _ts: Date.now() }));
    } catch {
        // quota / private mode
    }
}

export function clearEmbedPreviewSession(contentId: string): void {
    if (typeof localStorage !== 'undefined') {
        try {
            localStorage.removeItem(storageKey(contentId));
            localStorage.removeItem(`${LEGACY_LS_PREFIX}${contentId}`);
        } catch {
            // ignore
        }
    }
    if (typeof sessionStorage !== 'undefined') {
        try {
            sessionStorage.removeItem(storageKey(contentId));
            sessionStorage.removeItem(`${LEGACY_SESSION_KEY_PREFIX}${contentId}`);
        } catch {
            // ignore
        }
    }
}

/**
 * Remove stale preview session entries from localStorage.
 * Call at app startup to prevent unbounded storage growth.
 * @param maxAgeMs Maximum age in ms (default 24 hours)
 */
export function cleanupStalePreviewSessions(maxAgeMs = 24 * 60 * 60 * 1000): void {
    if (typeof localStorage === 'undefined') {
        return;
    }
    try {
        const now = Date.now();
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key || !key.startsWith(KEY_PREFIX)) {
                continue;
            }
            try {
                const raw = localStorage.getItem(key);
                if (!raw) {
                    continue;
                }
                const parsed = JSON.parse(raw) as { _ts?: number };
                // Remove if no timestamp (legacy) or if older than maxAgeMs
                if (!parsed._ts || now - parsed._ts > maxAgeMs) {
                    keysToRemove.push(key);
                }
            } catch {
                // Malformed entry — remove it
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => {
            try {
                localStorage.removeItem(key);
            } catch { /* ignore */ }
        });
        // Also clean up legacy keys
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key && key.startsWith(LEGACY_LS_PREFIX)) {
                try {
                    localStorage.removeItem(key);
                } catch { /* ignore */ }
            }
        }
    } catch {
        // ignore
    }
}

export function markEmbedPreviewSessionActivated(
    contentId: string,
    options?: ReadEmbedPreviewSessionOptions,
): void {
    const cur = readEmbedPreviewSession(contentId, options);
    if (!cur || cur.activated) {
        return;
    }
    writeEmbedPreviewSession(contentId, {
        session_id: cur.session_id,
        launch_token: null,
        activated: true,
        user_answers: cur.user_answers,
    });
}

/**
 * Merge a learner answer patch (same shape as session `append_answer`: `answer`, `h5p_*`, etc.).
 */
export function mergeEmbedPreviewAnswerFromPlayer(
    contentId: string,
    patch: Record<string, unknown>,
    sessionId?: string | null,
): void {
    const cur = readEmbedPreviewSession(contentId, {
        expectedSessionId: sessionId ?? undefined,
    });
    if (!cur) {
        return;
    }
    const prevUa = cur.user_answers ? { ...cur.user_answers } : {};
    const nextUa = merge({}, prevUa, patch) as Record<string, unknown>;
    writeEmbedPreviewSession(contentId, {
        ...cur,
        user_answers: nextUa,
    });
}

/** Base64 encode of `user_answers` for embed query `qbUa` (omit if too large). */
export function encodeUserAnswersForEmbedUrl(userAnswers: Record<string, unknown> | null): string | null {
    if (!userAnswers || Object.keys(userAnswers).length === 0) {
        return null;
    }
    try {
        const json = JSON.stringify(userAnswers);
        if (json.length > EMBED_USER_ANSWERS_MAX_URL_CHARS) {
            return null;
        }
        return btoa(unescape(encodeURIComponent(json)));
    } catch {
        return null;
    }
}

/** @deprecated Use readEmbedPreviewSession */
export const readQbViewSession = readEmbedPreviewSession;
/** @deprecated Use writeEmbedPreviewSession */
export const writeQbViewSession = writeEmbedPreviewSession;
/** @deprecated Use clearEmbedPreviewSession */
export const clearQbViewSession = clearEmbedPreviewSession;
/** @deprecated Use markEmbedPreviewSessionActivated */
export const markQbViewSessionActivated = markEmbedPreviewSessionActivated;
/** @deprecated Use mergeEmbedPreviewAnswerFromPlayer */
export const mergeQbAnswerFromPlayer = mergeEmbedPreviewAnswerFromPlayer;
/** @deprecated Use encodeUserAnswersForEmbedUrl */
export const encodeUserAnswersForUrl = encodeUserAnswersForEmbedUrl;
/** @deprecated Use EmbedPreviewSessionCache */
export type QbViewSessionCache = EmbedPreviewSessionCache;
